import React, { useState } from 'react';
import { connect } from 'react-redux';
import { Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom';

import { Drawer, Layout, Menu } from 'antd';
import type { MenuProps } from 'antd';

import { MenuOutlined } from '@ant-design/icons';

import { MenuInfo } from 'rc-menu/lib/interface';

import { HelpPage } from './help/HelpPage';
import IndexPage from './index/IndexPage';
import MonitorPage from './monitor/MonitorPage';
import ReportsPage from './reports/ReportsPage';
import FieldsPage from './fields/FieldsPage';
import FieldPage from './fields/FieldPage';
import SettingsPage from './settings/SettingsPage';
import SeasonsPage from './settings/SeasonsPage';
import AboutCompany from './aboutCompany/AboutCompany';
import AssistantPage from './assistant/AssistantPage';
import StationDataPage from './station_data/StationDataPage';
import ServicesPage from './services/ServicesPage';
import ProfilePage from './profile/ProfilePage';

import UserMenu from './components/UserMenu';
import logo from '../assets/logo.svg';
import CookieNotice from './components/CookieNotice';

import { User } from '../models/User';

const { Content, Footer, Header, Sider } = Layout;

type StringStringMap = Record<string, string>;

const pageTitles: StringStringMap = {
    '/': 'Главная',
    '/reports': 'Отчёты',
    '/assistant': 'Агропомощник',
    '/station-data': 'Данные станции',
    '/services': 'Услуги',
    '/profile': 'Личные данные',
    '/settings/fields': 'Настройки · Поля',
    '/settings/cultures': 'Настройки · Культуры',
    '/settings/devices': 'Настройки · Устройства',
    '/settings/seasons': 'Управление сезонами',
    '/help': 'Помощь',
    '/about_company': 'О компании',
    '/company_brief': 'Сводки',
    '/wiki': 'Вики',
    '/fields': 'Поля',
    '/monitor': 'Мониторинг',
    '/devices': 'Метеоустройства',
    '/digital_helper': 'Цифровой помощник',
    '/agromodels': 'Агромодели',
    '/notifications': 'Уведомления',
};

const getPageTitle = (pathname: string): string => {
    const fieldMatch = pathname.match(/^\/fields\/(\d+)/);
    if (fieldMatch) return `Поле №${fieldMatch[1]}`;
    return pageTitles[pathname] || 'Агропогода';
};

const getSelectedKey = (pathname: string): string => {
    if (pathname === '/settings' || pathname === '/settings/') return '/settings/fields';
    return pathname;
};

interface Props {
    user: User;
}

const ContentWrapper = (props: Props) => {
    const { user } = props;
    const location = useLocation();
    const navigate = useNavigate();
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [dynamicTitle, setDynamicTitle] = useState<string | null>(null);

    const isAdmin = !!user?.is_admin;

    const menuItems: MenuProps['items'] = [
        { key: '/', label: 'Главная' },
        { key: '/reports', label: 'Отчёты' },
        { key: '/assistant', label: 'Агропомощник' },
        { key: '/station-data', label: 'Данные станции' },
        { key: '/services', label: 'Услуги' },
        { type: 'divider' },
        {
            key: 'sub-settings',
            label: 'Настройки',
            children: [
                { key: '/settings/fields', label: 'Поля' },
                { key: '/settings/cultures', label: 'Культуры' },
                { key: '/settings/devices', label: 'Устройства' },
            ],
        },
        { type: 'divider' },
        { key: '/help', label: 'Помощь' },
        { key: '/about_company', label: 'О компании' },
        ...(isAdmin
            ? [
                  { type: 'divider' as const },
                  {
                      key: 'sub-service',
                      label: 'Служебное',
                      children: [
                          { key: '/company_brief', label: 'Сводки' },
                          { key: '/admin', label: 'Админка' },
                          { key: '/grafana', label: 'Графана' },
                          { key: '/wiki', label: 'Вики' },
                      ],
                  },
              ]
            : []),
    ];

    const handleMenuClick = (e: MenuInfo) => {
        if (e.key.startsWith('sub-')) return;
        navigate(e.key);
        setDrawerOpen(false);
    };

    const menu = (
        <Menu
            theme="light"
            mode="inline"
            onClick={handleMenuClick}
            selectedKeys={[getSelectedKey(location.pathname)]}
            defaultOpenKeys={['sub-settings']}
            items={menuItems}
            style={{ borderInlineEnd: 'none' }}
        />
    );

    const brand = (
        <div className="app-brand">
            <img className="app-brand-logo" src={logo} alt="Агропогода" />
            <div className="app-brand-title">Агропогода</div>
        </div>
    );

    return (
        <Layout style={{ minHeight: '100vh' }}>
            <Sider theme="light" className="app-sider" width={272}>
                {brand}
                {menu}
            </Sider>

            <Layout>
                <Header className="app-topbar">
                    <button
                        type="button"
                        className="app-burger"
                        onClick={() => setDrawerOpen(true)}
                        aria-label="Меню"
                    >
                        <MenuOutlined />
                    </button>
                    <div className="app-topbar-title">{dynamicTitle || getPageTitle(location.pathname)}</div>
                    <UserMenu />
                </Header>

                <Content className="site-layout-content">
                    <Routes>
                        <Route path="/" element={<IndexPage />} />
                        <Route path="/reports" element={<ReportsPage />} />
                        <Route path="/assistant" element={<AssistantPage />} />
                        <Route path="/station-data" element={<StationDataPage />} />
                        <Route path="/services" element={<ServicesPage />} />
                        <Route path="/profile" element={<ProfilePage />} />
                        <Route path="/settings" element={<Navigate to="/settings/fields" replace />} />
                        <Route path="/settings/fields" element={<SettingsPage />} />
                        <Route path="/settings/cultures" element={<SettingsPage />} />
                        <Route path="/settings/devices" element={<SettingsPage />} />
                        <Route path="/settings/seasons" element={<SeasonsPage />} />
                        <Route path="/fields" element={<FieldsPage />} />
                        <Route path="/fields/:id" element={<FieldPage setPageTitle={setDynamicTitle} />} />
                        <Route path="/monitor" element={<MonitorPage />} />
                        <Route path="/monitor/:id" element={<MonitorPage />} />
                        <Route path="/help" element={<HelpPage />} />
                        <Route path="/payment" element={<Navigate to="/settings" replace />} />
                        <Route path="/about_company" element={<AboutCompany />} />
                    </Routes>
                </Content>

                <Footer className="app-footer">
                    <div className="app-footer-links">© OOO "Пактема"</div>
                </Footer>
            </Layout>

            <Drawer
                placement="left"
                open={drawerOpen}
                onClose={() => setDrawerOpen(false)}
                width={280}
                styles={{ body: { padding: 0 } }}
                closable={false}
            >
                {brand}
                {menu}
            </Drawer>
            <CookieNotice />
        </Layout>
    );
};

const mapStateToProps = (state: any) => {
    return {
        user: state.user.user,
    };
};

export default connect(mapStateToProps)(ContentWrapper);
